// Araç Yükleme Talep ONAY EKRANI — master-detail (spec §6).
//
// Neden master-detail: onaycı günde 100+ talep bakıyor; her talep için ayrı sayfaya gitmek maliyetli.
// Solda c360 listesi, sağda VehicleLoadApprovalPanel (uyarı bandı + kalem revizesi + Plasiyer 360).
// Tam-sayfa detay (/vehicle-loads/:id) DURUYOR — düzenleme/geçmiş için hâlâ kullanılır.
//
// KRİTİK: "Bekleme Süresi" kolonu. Plasiyer YÜKLEME GÜNÜ SABAH 04:00'te depoda olur (spec §0), yani
// onayın gece bitmiş olması gerekir. Deadline'a yaklaşan talepler KIRMIZI ve VARSAYILAN SIRALAMA
// bu kolona göredir — onaycı en riskliyi en üstte görür.
//
// ══ VERİ KATMANI: OData (proje kuralı: listeler OData) ═══════════════════════════════════════════
// Liste `GET /api/vehicles/loads/odata` üzerinden `useC360List` ile beslenir: SUNUCU tarafı
// sayfalama + sıralama + filtre. Eskiden `take=500` çekilip İSTEMCİDE süzülüyordu — 501. talep
// hiçbir filtreyle bulunamıyordu ve onay kuyruğu sessizce kırpılıyordu. Süzülen HER ALAN zaten
// VehicleLoadHeader kolonu (Status, Plate, SdmName, RequestType, CreditLimitStatus, ShipmentDate…).
//
// "Bekleme Süresi" TÜRETİLMİŞTİR (sunucuda kolonu yok) → sıralaması `SentAt asc`'a çevrilir:
// en eski gönderilen = en uzun bekleyen = en riskli, EN ÜSTTE. Kolonun kendisi (ve 04:00 deadline
// kırmızısı) `waitingInfo` ile SentAt/ShipmentDate'ten türetilmeye devam eder.
//
// Rozet sayaçları da sunucudan gelir ($apply=groupby((Status),aggregate($count as Count))):
// sayfalanan bir listede "bu sayfada 3 bekleyen var" demek "3 bekleyen var" DEMEK DEĞİLDİR.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, ColumnChooser, ViewIcon, unwrapLookupItems, useC360List, escapeODataString,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { VehicleLoadApprovalPanel } from './vehicleload/VehicleLoadApprovalPanel';
import {
  API_BASE, STATUS_META, STATUS_OPTIONS, REQUEST_TYPE_OPTIONS,
  CREDIT_LIMIT_OPTIONS, pick, statusBadge, limitBadge,
  formatDate, formatMoney, formatNumber, shipmentDateOf,
  waitingInfo, isPendingStatus,
} from './vehicleload/vehicleLoadShared';
import './vehicleload/vehicleLoad.css';

const ODATA_BASE = `${API_BASE}/odata`;

const dateOnly = (d) => d.toISOString().slice(0, 10);
const today = () => dateOnly(new Date());
const tomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return dateOnly(d); };

// "Bekleyen" kümesi TEK KAYNAKTAN (vehicleLoadShared.isPendingStatus) türetilir — burada
// ikinci bir liste tutulsaydı iki dosya sessizce ayrışırdı.
const PENDING_STATUSES = STATUS_OPTIONS.map((o) => o.value).filter(isPendingStatus);

// Yükleme tarihi varsayılanı: BUGÜN + YARIN (spec §6.1) — onay kuyruğu ertesi güne bakar.
const DEFAULT_FILTERS = {
  shipFrom: today(),
  shipTo: tomorrow(),
  reqFrom: '',
  reqTo: '',
  region: '',
  warehouseId: '',
  representativeId: '',
  plate: '',
  statuses: [],           // çoklu seçim
  requestType: '',
  creditLimitStatus: '',
  onlyPending: true,      // varsayılan AÇIK (spec §6.1)
};

const dayStart = (d) => `${d}T00:00:00Z`;
const dayEnd = (d) => `${d}T23:59:59Z`;
const containsClause = (field, value) => `contains(tolower(${field}),'${escapeODataString(value).toLowerCase()}')`;
const statusInClause = (values) => `(${values.map((v) => `Status eq '${v}'`).join(' or ')})`;

/** Toolbar filtreleri → OData $filter (kolon hunileri + arama bununla VE'lenir). */
function buildBaseFilter(f) {
  const c = [];

  if (f.onlyPending) {
    c.push(statusInClause(PENDING_STATUSES));
  } else if (f.statuses.length > 0) {
    c.push(statusInClause(f.statuses));
  }

  // Yükleme tarihi aralığı — tarihi OLMAYAN talep ELENMEZ (eski kayıtlar gizlenmesin; legacy
  // DeliveryNote string'i ShipmentDate'e taşınmamış olabilir).
  if (f.shipFrom) c.push(`(ShipmentDate eq null or ShipmentDate ge ${dayStart(f.shipFrom)})`);
  if (f.shipTo) c.push(`(ShipmentDate eq null or ShipmentDate le ${dayEnd(f.shipTo)})`);

  if (f.reqFrom) c.push(`CreatedDate ge ${dayStart(f.reqFrom)}`);
  if (f.reqTo) c.push(`CreatedDate le ${dayEnd(f.reqTo)}`);

  if (f.region) c.push(containsClause('SdmName', f.region));
  if (f.plate) c.push(containsClause('Plate', f.plate));
  if (f.warehouseId) c.push(`WarehouseId eq ${f.warehouseId}`);
  if (f.representativeId) c.push(`RepresentativeId eq ${f.representativeId}`);
  if (f.requestType) c.push(`RequestType eq '${escapeODataString(f.requestType)}'`);
  if (f.creditLimitStatus) c.push(`CreditLimitStatus eq '${escapeODataString(f.creditLimitStatus)}'`);

  return c.map((x) => `(${x})`).join(' and ');
}

// Kolon hunileri — HEPSİ sunucuda süzülür. `representativeName` türetilmiş bir kolondur ama
// kaynağı gerçek bir FK'dır (RepresentativeId) → huni onu süzer.
const COLUMN_FILTER_META = {
  sdmName: { type: 'text', odataField: 'SdmName' },
  representativeName: { type: 'select', odataField: 'RepresentativeId', valueFormat: 'guid' },
  plate: { type: 'text', odataField: 'Plate' },
  distinctSku: { type: 'number', odataField: 'DistinctSku' },
  totalCases: { type: 'number', odataField: 'TotalCases' },
  creditLimitStatus: { type: 'select', odataField: 'CreditLimitStatus', valueFormat: 'string', options: CREDIT_LIMIT_OPTIONS },
  status: { type: 'select', odataField: 'Status', valueFormat: 'string', options: STATUS_OPTIONS },
};

const SEARCH_FIELDS = ['RequestNo'];

export function VehicleLoadsPage() {
  usePageMeta('Araç Yükleme Talep Onayı', 'Tüccar plasiyerin araç yükleme taleplerinin onay kuyruğu.');
  const navigate = useNavigate();
  const { canEdit } = usePageActions();

  const [toast, setToast] = useState(null);
  const [selected, setSelected] = useState(null);       // master-detail seçili satır
  const [checkedIds, setCheckedIds] = useState(() => new Set()); // toplu onay seçimi
  const [columnVisibility, setColumnVisibility] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, received: 0, cancelled: 0 });

  const [representatives, setRepresentatives] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const baseFilter = useMemo(() => buildBaseFilter(filters), [filters]);

  const list = useC360List({
    apiBase: ODATA_BASE,
    baseFilter,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    // Bekleme Süresi = türetilmiş; sunucudaki karşılığı SentAt'tir. asc = en eski gönderilen
    // (= en uzun bekleyen, 04:00'a en yakın) EN ÜSTTE.
    defaultSortField: 'SentAt',
    defaultSortDir: 'asc',
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  // Rozet sayaçları: SUNUCU gerçeği (sayfadan bağımsız) — tek $apply isteği.
  const loadCounts = useCallback(async () => {
    try {
      const applyExpr = 'groupby((Status),aggregate($count as Count))';
      const res = await fetch(`${ODATA_BASE}?$apply=${encodeURIComponent(applyExpr)}`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      const byStatus = {};
      (data?.value || []).forEach((g) => {
        const st = pick(g, 'Status', 'status');
        if (st) byStatus[st] = Number(pick(g, 'Count', 'count')) || 0;
      });
      const sum = (...keys) => keys.reduce((acc, k) => acc + (byStatus[k] || 0), 0);
      setCounts({
        pending: sum(...PENDING_STATUSES),
        approved: sum('APPROVED', 'PARTIALLY_APPROVED', 'READY_FOR_PICKUP'),
        rejected: sum('REJECTED'),
        received: sum('RECEIVED', 'RECEIVED_WITH_DIFF', 'LOADED'),
        cancelled: sum('CANCELLED'),
      });
    } catch { /* rozet kritik değil — liste yine çalışır */ }
  }, []);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  const refreshAll = useCallback(() => { fetchData(); loadCounts(); }, [fetchData, loadCounts]);

  // Plasiyer / depo adları liste ucundan GELMİYOR (yalnız id) → lookup'tan çözülür.
  useEffect(() => {
    const headers = authHeaders();
    fetch('/api/representatives/Select', { headers })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setRepresentatives(unwrapLookupItems(d)))
      .catch(() => setRepresentatives([]));
    fetch('/api/warehouses/Select', { headers })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setWarehouses(unwrapLookupItems(d)))
      .catch(() => setWarehouses([]));
  }, []);

  const repNameById = useMemo(() => {
    const map = new Map();
    representatives.forEach((r) => map.set(String(pick(r, 'id', 'Id')), pick(r, 'name', 'Name', 'text', 'Text') || ''));
    return map;
  }, [representatives]);
  const whNameById = useMemo(() => {
    const map = new Map();
    warehouses.forEach((w) => map.set(String(pick(w, 'id', 'Id')), pick(w, 'name', 'Name', 'text', 'Text') || ''));
    return map;
  }, [warehouses]);

  const setFilter = (k, v) => { setFilters((c) => ({ ...c, [k]: v })); setCurrentPage(1); };
  const toggleStatus = (value) => {
    setFilters((c) => ({
      ...c,
      statuses: c.statuses.includes(value) ? c.statuses.filter((x) => x !== value) : [...c.statuses, value],
    }));
    setCurrentPage(1);
  };

  // Toplu onay: uyarısı olan talepleri backend ATLAR ve kaç tanesini atladığını söyler (spec §6.4).
  const handleBulkApprove = async () => {
    const ids = [...checkedIds];
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await fetch(`${API_BASE}/bulk-approve`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ ids }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(payload, 'Message', 'message') || `Toplu onay başarısız (${res.status})`);
      const data = pick(payload, 'Data', 'data') ?? payload ?? {};
      const ok = pick(data, 'approvedCount', 'ApprovedCount') ?? 0;
      const skipped = pick(data, 'skippedCount', 'SkippedCount') ?? 0;
      showToast(
        skipped > 0 ? 'info' : 'success',
        skipped > 0
          ? `${ok} talep onaylandı · ${skipped} talep ATLANDI (uyarısı olan / durumu uygun olmayan). Tek tek inceleyin.`
          : `${ok} talep onaylandı`,
      );
      setCheckedIds(new Set());
      refreshAll();
    } catch (err) {
      showToast('error', err.message || 'Toplu onay başarısız');
    } finally {
      setBulkBusy(false);
    }
  };

  const toggleCheck = (id) => setCheckedIds((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const columnHelper = createColumnHelper();
  // "Tümünü seç" GÖRÜNEN SAYFAYI seçer (liste artık sunucuda sayfalanıyor; görünmeyen bir
  // kaydı toplu onaya sokmak sessiz bir onay olurdu).
  const pageIds = useMemo(() => rows.map((r) => String(pick(r, 'id', 'Id'))), [rows]);
  const allChecked = pageIds.length > 0 && pageIds.every((id) => checkedIds.has(id));

  const columns = useMemo(() => [
    columnHelper.display({
      id: 'select', enableHiding: false, enableSorting: false,
      header: () => (
        <input
          type="checkbox" checked={allChecked}
          onChange={(e) => setCheckedIds(e.target.checked ? new Set(pageIds) : new Set())}
          onClick={(e) => e.stopPropagation()}
          aria-label="Tümünü seç"
        />
      ),
      cell: ({ row }) => {
        const id = String(pick(row.original, 'id', 'Id'));
        return (
          <input
            type="checkbox" checked={checkedIds.has(id)}
            onChange={() => toggleCheck(id)}
            onClick={(e) => e.stopPropagation()}
            aria-label="Talebi seç"
          />
        );
      },
    }),
    columnHelper.accessor('requestNo', {
      header: 'Talep No', enableColumnFilter: false,
      cell: (info) => <span className="vl-mono">{info.getValue() || '—'}</span>,
    }),
    columnHelper.accessor('shipmentDate', {
      header: 'Yükleme Tarihi', enableColumnFilter: false,
      cell: ({ row }) => formatDate(shipmentDateOf(row.original)),
    }),
    columnHelper.accessor('sdmName', {
      header: 'Bölge', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.display({
      id: 'representativeName', header: 'Plasiyer', enableSorting: false, meta: { filterType: 'select' },
      cell: ({ row }) => repNameById.get(String(pick(row.original, 'representativeId', 'RepresentativeId'))) || '—',
    }),
    columnHelper.accessor('plate', {
      header: 'Araç', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('distinctSku', {
      header: 'Kalem', meta: { filterType: 'number' },
      cell: (info) => info.getValue() ?? '-',
    }),
    columnHelper.accessor('totalCases', {
      header: 'Toplam Koli', meta: { filterType: 'number' },
      cell: (info) => info.getValue() ?? '-',
    }),
    columnHelper.accessor('totalWeightKg', {
      header: 'Ağırlık', enableColumnFilter: false,
      cell: (info) => (info.getValue() == null ? '-' : `${formatNumber(info.getValue(), 1)} kg`),
    }),
    columnHelper.accessor('capacityUsagePct', {
      header: 'Kapasite %', enableColumnFilter: false,
      cell: (info) => {
        const v = Number(info.getValue() || 0);
        if (!v) return '-';
        const cls = v > 100 ? 'vl-cap--over' : v >= 90 ? 'vl-cap--high' : undefined;
        return <span className={cls}>%{formatNumber(v, 1)}</span>;
      },
    }),
    columnHelper.accessor('netAmount', {
      header: 'Tutar', enableColumnFilter: false,
      cell: (info) => formatMoney(info.getValue()),
    }),
    columnHelper.accessor('creditLimitStatus', {
      header: 'Limit', meta: { filterType: 'select' },
      cell: (info) => limitBadge(info.getValue()),
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => statusBadge(info.getValue()),
    }),
    // KRİTİK kolon: 04:00 deadline'ına yaklaşan talepler kırmızı. Değer TÜRETİLMİŞTİR;
    // sunucu-tarafı sıralaması kolon kimliği üzerinden SentAt'e düşer (asc = en uzun bekleyen üstte).
    columnHelper.accessor('sentAt', {
      id: 'sentAt', header: 'Bekleme Süresi', enableColumnFilter: false,
      cell: ({ row }) => {
        const info = waitingInfo(row.original);
        if (info.elapsedMin == null) return <span style={{ color: '#94a3b8' }}>—</span>;
        const cls = info.tone === 'crit' ? 'vl-wait--crit' : info.tone === 'warn' ? 'vl-wait--warn' : undefined;
        const title = info.remainingMin == null
          ? 'Yükleme tarihi yok — deadline hesaplanamadı'
          : info.remainingMin < 0 ? '04:00 deadline’ı GEÇTİ' : `04:00’a ${Math.floor(info.remainingMin / 60)}s kaldı`;
        return <span className={cls} title={title}>{info.text}</span>;
      },
    }),
    columnHelper.accessor('sapOrderNo', {
      header: 'SAP No', enableColumnFilter: false,
      cell: (info) => <span className="vl-mono">{info.getValue() || '—'}</span>,
    }),
    columnHelper.accessor('approvedBy', {
      header: 'Onaycı', enableColumnFilter: false,
      cell: (info) => info.getValue() || '—',
    }),
    columnHelper.display({
      id: 'actions', header: '', enableHiding: false, enableSorting: false,
      cell: ({ row }) => (
        <span className="c360-actions" onClick={(e) => e.stopPropagation()}>
          <button
            type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view"
            title="Tam sayfa detay" aria-label="Tam sayfa detay"
            onClick={() => navigate(`/vehicle-loads/${pick(row.original, 'id', 'Id')}`, { state: { row: row.original } })}
          >
            <ViewIcon />
          </button>
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [checkedIds, allChecked, pageIds, repNameById, navigate]);

  // Huni içeriği: Plasiyer = lookup (runtime), diğerleri meta'daki sabit seçenekler.
  const repOptions = useMemo(
    () => representatives.map((r) => {
      const id = String(pick(r, 'id', 'Id'));
      return { value: id, label: repNameById.get(id) || id };
    }),
    [representatives, repNameById],
  );
  const resolveFilterProps = useCallback((columnId, meta) => (
    columnId === 'representativeName' ? { options: repOptions } : { options: meta.options }
  ), [repOptions]);

  const sorting = useMemo(
    () => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []),
    [sortField, sortDir],
  );
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('SentAt', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(pick(row, 'id', 'Id')),
  });

  // Dışa aktarım: FİLTRELİ TÜM kayıtlar (görünen sayfa değil) — sunucudan iner.
  const handleExport = async () => {
    const cols = [
      { field: 'requestNo', headerName: 'Talep No' },
      { headerName: 'Yükleme Tarihi', valueGetter: ({ row }) => formatDate(shipmentDateOf(row)) },
      { field: 'sdmName', headerName: 'Bölge' },
      { headerName: 'Plasiyer', valueGetter: ({ row }) => repNameById.get(String(pick(row, 'representativeId', 'RepresentativeId'))) || '' },
      { field: 'plate', headerName: 'Araç' },
      { field: 'distinctSku', headerName: 'Kalem' },
      { field: 'totalCases', headerName: 'Toplam Koli' },
      { field: 'totalWeightKg', headerName: 'Ağırlık (kg)' },
      { field: 'capacityUsagePct', headerName: 'Kapasite %' },
      { field: 'netAmount', headerName: 'Tutar' },
      { field: 'creditLimitStatus', headerName: 'Limit' },
      { headerName: 'Durum', valueGetter: ({ row }) => (STATUS_META[pick(row, 'status', 'Status')]?.label || pick(row, 'status', 'Status') || '-') },
      { headerName: 'Bekleme', valueGetter: ({ row }) => waitingInfo(row).text },
      { field: 'sapOrderNo', headerName: 'SAP No' },
      { field: 'approvedBy', headerName: 'Onaycı' },
    ];
    try {
      const n = await exportRows(cols, `arac-yukleme-onay_${today()}`);
      showToast(n ? 'success' : 'error', n ? `${n} kayıt dışa aktarıldı` : 'Dışa aktarılacak veri yok');
    } catch (err) {
      showToast('error', err.message || 'Dışa aktarım başarısız');
    }
  };

  const toolbarNode = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
      <div className="vl-filters">
        <div className="vl-flt">
          <label htmlFor="vl-ship-from">Yükleme tarihi</label>
          <span style={{ display: 'inline-flex', gap: 4 }}>
            <input id="vl-ship-from" type="date" value={filters.shipFrom} onChange={(e) => setFilter('shipFrom', e.target.value)} />
            <input type="date" value={filters.shipTo} onChange={(e) => setFilter('shipTo', e.target.value)} aria-label="Yükleme tarihi bitiş" />
          </span>
        </div>
        <div className="vl-flt">
          <label htmlFor="vl-req-from">Talep tarihi</label>
          <span style={{ display: 'inline-flex', gap: 4 }}>
            <input id="vl-req-from" type="date" value={filters.reqFrom} onChange={(e) => setFilter('reqFrom', e.target.value)} />
            <input type="date" value={filters.reqTo} onChange={(e) => setFilter('reqTo', e.target.value)} aria-label="Talep tarihi bitiş" />
          </span>
        </div>
        <div className="vl-flt">
          <label htmlFor="vl-region">Bölge</label>
          <input id="vl-region" type="text" placeholder="SDM / bölge" value={filters.region} onChange={(e) => setFilter('region', e.target.value)} />
        </div>
        <div className="vl-flt">
          <label htmlFor="vl-wh">Depo</label>
          <select id="vl-wh" value={filters.warehouseId} onChange={(e) => setFilter('warehouseId', e.target.value)}>
            <option value="">Tümü</option>
            {warehouses.map((w) => {
              const id = String(pick(w, 'id', 'Id'));
              return <option key={id} value={id}>{whNameById.get(id) || id}</option>;
            })}
          </select>
        </div>
        <div className="vl-flt">
          <label htmlFor="vl-rep">Plasiyer</label>
          <select id="vl-rep" value={filters.representativeId} onChange={(e) => setFilter('representativeId', e.target.value)}>
            <option value="">Tümü</option>
            {representatives.map((r) => {
              const id = String(pick(r, 'id', 'Id'));
              return <option key={id} value={id}>{repNameById.get(id) || id}</option>;
            })}
          </select>
        </div>
        <div className="vl-flt">
          <label htmlFor="vl-plate">Araç</label>
          <input id="vl-plate" type="text" placeholder="Plaka" value={filters.plate} onChange={(e) => setFilter('plate', e.target.value)} style={{ minWidth: 100 }} />
        </div>
        <div className="vl-flt">
          <label htmlFor="vl-type">Talep tipi</label>
          <select id="vl-type" value={filters.requestType} onChange={(e) => setFilter('requestType', e.target.value)}>
            <option value="">Tümü</option>
            {REQUEST_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="vl-flt">
          <label htmlFor="vl-limit">Limit durumu</label>
          <select id="vl-limit" value={filters.creditLimitStatus} onChange={(e) => setFilter('creditLimitStatus', e.target.value)}>
            <option value="">Tümü</option>
            {CREDIT_LIMIT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="vl-flt">
          <label htmlFor="vl-search">Ara</label>
          <input
            id="vl-search" type="text" placeholder="Talep no"
            value={filterInput}
            onChange={(e) => setFilterInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
            style={{ minWidth: 190 }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          type="button"
          className={`vl-chip${filters.onlyPending ? ' is-on' : ''}`}
          onClick={() => setFilter('onlyPending', !filters.onlyPending)}
        >
          Sadece bekleyenler
        </button>
        <span style={{ width: 1, height: 18, background: 'var(--border-color, rgba(15,23,42,0.12))' }} />
        {/* Durum (çoklu) */}
        {STATUS_OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`vl-chip${filters.statuses.includes(o.value) ? ' is-on' : ''}`}
            onClick={() => toggleStatus(o.value)}
            disabled={filters.onlyPending}
            title={filters.onlyPending ? '“Sadece bekleyenler” açıkken durum seçilemez' : undefined}
          >
            {o.label}
          </button>
        ))}
        <button
          type="button" style={s.btnSecondary}
          onClick={() => {
            setFilters(DEFAULT_FILTERS);
            setFilterInput('');
            applyFilterText('');
            Object.keys(columnFilters).forEach((id) => clearColumnFilter(id));
            setCurrentPage(1);
          }}
        >
          Filtreleri Sıfırla
        </button>
        <button type="button" style={s.btnSecondary} onClick={refreshAll}>Yenile</button>
        <ColumnChooser table={table} />
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          {filterText && (
            <button type="button" style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>
              Aramayı Temizle
            </button>
          )}
          {checkedIds.size > 0 && canEdit && (
            <button
              type="button"
              style={{ ...s.btnPrimary, background: '#16a34a', borderColor: '#16a34a', opacity: bulkBusy ? 0.6 : 1 }}
              disabled={bulkBusy}
              onClick={handleBulkApprove}
            >
              {bulkBusy ? 'Onaylanıyor…' : `Seçili ${checkedIds.size} talebi toplu onayla`}
            </button>
          )}
          <button
            type="button"
            style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}
          >
            <ExcelIcon size={16} />
          </button>
        </span>
      </div>
    </div>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  const selectedId = selected ? String(pick(selected, 'id', 'Id')) : null;

  return (
    <div style={s.container}>
      <div className="vl-split">
        <div className="vl-split-list">
          <ListPageShell
            title="Araç Yükleme Talep Onayı"
            subtitle="Tüccar plasiyerin el terminalinden gönderdiği yükleme talepleri. Onay 04:00 depo teslimatından ÖNCE bitmelidir."
            actions={(
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <StatusBadge tone="wait">Bekleyen: {counts.pending}</StatusBadge>
                <StatusBadge tone="ok">Onaylı: {counts.approved}</StatusBadge>
                <StatusBadge tone="ok">Teslim: {counts.received}</StatusBadge>
                <StatusBadge tone="bad">Red: {counts.rejected}</StatusBadge>
                <StatusBadge tone="bad">İptal: {counts.cancelled}</StatusBadge>
              </div>
            )}
            toolbar={toolbarNode}
            footer={footerNode}
          >
            <C360Table
              table={table}
              columnFilterMeta={COLUMN_FILTER_META}
              columnFilters={columnFilters}
              onApplyFilter={setColumnFilter}
              onClearFilter={clearColumnFilter}
              resolveFilterProps={resolveFilterProps}
              loading={loading}
              selected={selected}
              onRowClick={setSelected}
              onRowDoubleClick={(row) => navigate(`/vehicle-loads/${pick(row, 'id', 'Id')}`, { state: { row } })}
              emptyText="Kayıt bulunamadı (filtreleri sıfırlamayı deneyin)"
            />
          </ListPageShell>
        </div>

        <aside className="vl-split-detail">
          {selectedId ? (
            <VehicleLoadApprovalPanel
              key={selectedId}
              loadId={selectedId}
              canEdit={canEdit}
              showToast={showToast}
              onChanged={refreshAll}
            />
          ) : (
            <div className="vl-card vl-empty">
              Soldaki listeden bir talep seçin — onay paneli burada açılır.
            </div>
          )}
        </aside>
      </div>

      <Toast toast={toast} />
    </div>
  );
}

export default VehicleLoadsPage;
